/**
 * Carregamento das estações e revalidação periódica.
 *
 * Os arquivos só são baixados quando a camada é ligada pela primeira vez.
 * Depois, o gancho da atualização automática do loop revalida — a máquina
 * publica a cada 5 min, os arquivos vão com `no-cache` e o servidor responde
 * 304 quando nada mudou, então revalidar a cada minuto é barato. A
 * revalidação é silenciosa: "Baixando…" piscando no painel seria ruído.
 */
import { useNow } from '../ui/hooks';
import { BASE_METEO, carregarDados } from './api';
import { frescorDe } from './frescor';
import { useMeteoStore } from './store';

/** Revalidações a menos disto do último pedido são ignoradas. */
const INTERVALO_MIN_MS = 55_000;

let emVoo: AbortController | null = null;
let jaCarregou = false;
let ultimoPedido = 0;

export async function carregarMeteo(force = false): Promise<void> {
  if (!BASE_METEO) return;
  const { situacao } = useMeteoStore.getState();
  if (!force && (situacao.estado === 'loading' || jaCarregou)) return;

  emVoo?.abort();
  const ctrl = new AbortController();
  emVoo = ctrl;
  ultimoPedido = Date.now();
  if (!jaCarregou) useMeteoStore.getState().setSituacao({ estado: 'loading', erro: null });

  try {
    const dados = await carregarDados(ctrl.signal);
    if (ctrl.signal.aborted) return;
    jaCarregou = true;
    // Publicação igual à da tela: não trocar `dados` poupa a reconstrução das
    // features e todos os `useMemo` que dependem dela.
    const atual = useMeteoStore.getState().dados;
    if (dados.gerado === null || dados.gerado !== atual.gerado) useMeteoStore.getState().setDados(dados);
    useMeteoStore.getState().setSituacao({ estado: 'ready', erro: null });
  } catch (e) {
    if (ctrl.signal.aborted) return;
    // Revalidação que falhou com dado na tela: mantém o dado e mostra o erro.
    useMeteoStore.getState().setSituacao({ estado: 'error', erro: e instanceof Error ? e.message : String(e) });
  } finally {
    if (emVoo === ctrl) emVoo = null;
  }
}

/** Revalidação do ciclo automático — só com a camada carregada e visível. */
export function refreshMeteo(): void {
  if (!useMeteoStore.getState().visivel) return;
  const nuncaCarregou = !jaCarregou && useMeteoStore.getState().situacao.estado === 'error';
  if (!jaCarregou && !nuncaCarregou) return;
  if (Date.now() - ultimoPedido < INTERVALO_MIN_MS) return;
  void carregarMeteo(true);
}

export function iniciarSyncMeteo(): () => void {
  if (useMeteoStore.getState().visivel) void carregarMeteo();
  const aoVoltarConexao = () => {
    ultimoPedido = 0;
    refreshMeteo();
  };
  if (typeof window !== 'undefined') window.addEventListener('online', aoVoltarConexao);
  const parar = useMeteoStore.subscribe((a, b) => {
    if (!a.visivel || b.visivel) return;
    // Religar depois de um tempo: o dado guardado pode ser de horas atrás.
    if (!jaCarregou) void carregarMeteo();
    else if (Date.now() - ultimoPedido >= INTERVALO_MIN_MS) void carregarMeteo(true);
  });
  return () => {
    parar();
    if (typeof window !== 'undefined') window.removeEventListener('online', aoVoltarConexao);
  };
}

/** Ponto amarelo no trilho: camada ligada e dado com problema. */
export function useMeteoDesatualizado(): boolean {
  const visivel = useMeteoStore((s) => s.visivel);
  const erro = useMeteoStore((s) => s.situacao.estado === 'error');
  const gerado = useMeteoStore((s) => s.dados.gerado);
  const maisNova = useMeteoStore((s) => s.dados.maisNova);
  // Relógio próprio: com a máquina parada os dados não mudam.
  const agora = useNow(60_000);
  if (!visivel) return false;
  const f = frescorDe(gerado, maisNova, agora);
  return erro || f === 'atrasado' || f === 'velho';
}

/** Só para os testes. */
export function resetMeteoSync(): void {
  emVoo?.abort();
  emVoo = null;
  jaCarregou = false;
  ultimoPedido = 0;
}
